import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "../../Collections/Imports";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { filterSlots, MANUAL_GROUP } from "./treeLogic";

/** Number input that commits on blur or Enter, so a row does not jump to another tier while staff type. */
const RankInput = ({ label, value, error, disabled, onCommit, onPending, pendingKey, width = 76 }) => (
  <TextField
    key={`${label}-${value ?? ""}`}
    size="small"
    label={label}
    defaultValue={value ?? ""}
    disabled={disabled}
    error={!!error}
    helperText={error || " "}
    inputProps={{ inputMode: "decimal", "aria-label": label }}
    sx={{ width, "& .MuiFormHelperText-root": { mx: 0, maxWidth: 160, whiteSpace: "normal" } }}
    onChange={(e) => onPending?.(pendingKey, String(e.target.value) !== String(value ?? ""))}
    onBlur={(e) => {
      onPending?.(pendingKey, false);
      if (String(e.target.value) !== String(value ?? "")) onCommit(e.target.value);
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") e.target.blur();
    }}
  />
);

const Action = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" aria-label={title} onClick={onClick} disabled={disabled}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * The tree as tier groups, each campaign with buyer, price, priority and status, in the order the API tries them.
 * Display filters hide rows but never change positions (the # column is the real walk position).
 */
const TierGroups = ({ groups, positions, filters, readOnly, errors, onAction, onField, onMoveTier, onPending }) => {
  if (!groups.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography>No campaigns in this tree yet. Add campaigns above or import a tree file.</Typography>
      </Paper>
    );
  }
  return (
    <Stack spacing={2}>
      {groups.map((group) => {
        const visible = filterSlots(group.campaigns, filters);
        const manual = group.tier === MANUAL_GROUP;
        const roleGroups = groups.filter((g) => !!g.exitOffer === !!group.exitOffer);
        const roleIndex = roleGroups.indexOf(group);
        const tierName = manual ? "Manual order (within each role)" : group.tier === null ? "No tier (tried last)" : `Tier ${group.tier}`;
        return (
          <Paper key={`${group.tier}-${group.exitOffer ? "exit" : "posted"}`} variant="outlined" sx={{ p: 1.5 }} data-testid={`tier-${group.tier ?? "none"}${group.exitOffer ? "-exit" : ""}`}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                {tierName}{group.exitOffer ? " · Exit offers" : ""}
              </Typography>
              {group.label && <Chip size="small" label={group.label} color="primary" variant="outlined" />}
              <Typography variant="body2" color="text.secondary">
                {group.campaigns.length} campaign{group.campaigns.length === 1 ? "" : "s"}
                {visible.length !== group.campaigns.length ? ` (${group.campaigns.length - visible.length} hidden by filters)` : ""}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {!readOnly && group.tier !== null && !manual && (
                <>
                  <Action title="Move tier up" onClick={() => onMoveTier(group.tier, "up", group.exitOffer === true)} disabled={roleIndex === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </Action>
                  <Action
                    title="Move tier down"
                    onClick={() => onMoveTier(group.tier, "down", group.exitOffer === true)}
                    disabled={roleIndex === roleGroups.length - 1 || roleGroups[roleIndex + 1]?.tier === null}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </Action>
                </>
              )}
            </Stack>
            {visible.map((s) => {
              const rowErrors = errors[s.campaignId] || {};
              return (
                <Stack
                  key={s.campaignId}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{ py: 0.5, borderTop: "1px solid #eee", opacity: s.active && !s.capped ? 1 : 0.6, flexWrap: "wrap" }}
                  data-testid={`row-${s.campaignId}`}
                >
                  <Typography sx={{ width: 36, pt: 1, fontWeight: 600 }} title="Walk position">
                    #{positions[s.campaignId]}
                  </Typography>
                  <Box sx={{ minWidth: 200, flex: 1, pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.campaignName || s.campaignId}{" "}
                      <Typography component="span" variant="caption" color="text.secondary">
                        #{s.uniqueId}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.buyerName || "unknown buyer"}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                      {!s.active && <Chip size="small" color="warning" label="Paused: skipped" />}
                      {s.capped && <Chip size="small" color="warning" label="Capped: skipped" />}
                      {s.alsoInTrees?.length > 0 && (
                        <Tooltip title="Tier and priority are set per tree. Price, weight and product are stored on the campaign: a change here also applies in these trees.">
                          <Chip size="small" variant="outlined" label={`Also in tree ${s.alsoInTrees.join(", ")}`} />
                        </Tooltip>
                      )}
                    </Stack>
                    {rowErrors.row && (
                      <Typography variant="caption" color="error" display="block">
                        {rowErrors.row}
                      </Typography>
                    )}
                  </Box>
                  <RankInput label="Tier" value={s.tier} onPending={onPending} pendingKey={`${s.campaignId}:tier`} error={rowErrors.tier} disabled={readOnly} onCommit={(v) => onField(s.campaignId, "tier", v)} width={70} />
                  <RankInput label="Price $" value={s.price} onPending={onPending} pendingKey={`${s.campaignId}:price`} error={rowErrors.price} disabled={readOnly} onCommit={(v) => onField(s.campaignId, "price", v)} width={90} />
                  <RankInput label="Priority" value={s.priority} onPending={onPending} pendingKey={`${s.campaignId}:priority`} error={rowErrors.priority} disabled={readOnly} onCommit={(v) => onField(s.campaignId, "priority", v)} width={80} />
                  {!readOnly && manual && (
                    <Box sx={{ pt: 0.5 }}>
                      <Action title="Up" onClick={() => onAction("listUp", s.campaignId)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </Action>
                      <Action title="Down" onClick={() => onAction("listDown", s.campaignId)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </Action>
                      <Action title="To top" onClick={() => onAction("listTop", s.campaignId)}>
                        <KeyboardDoubleArrowUpIcon fontSize="small" />
                      </Action>
                      <Action title="To bottom" onClick={() => onAction("listBottom", s.campaignId)}>
                        <KeyboardDoubleArrowDownIcon fontSize="small" />
                      </Action>
                      <Action title="Remove from tree" onClick={() => onAction("remove", s.campaignId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </Action>
                    </Box>
                  )}
                  {!readOnly && !manual && (
                    <Box sx={{ pt: 0.5 }}>
                      <Action title="Up (same price)" onClick={() => onAction("up", s.campaignId)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </Action>
                      <Action title="Down (same price)" onClick={() => onAction("down", s.campaignId)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </Action>
                      <Action title="Top of tier" onClick={() => onAction("tierTop", s.campaignId)}>
                        <VerticalAlignTopIcon fontSize="small" />
                      </Action>
                      <Action title="Bottom of tier" onClick={() => onAction("tierBottom", s.campaignId)}>
                        <VerticalAlignBottomIcon fontSize="small" />
                      </Action>
                      <Action title="To top of tree (own first tier)" onClick={() => onAction("treeTop", s.campaignId)}>
                        <KeyboardDoubleArrowUpIcon fontSize="small" />
                      </Action>
                      <Action title="To bottom of tree (own last tier)" onClick={() => onAction("treeBottom", s.campaignId)}>
                        <KeyboardDoubleArrowDownIcon fontSize="small" />
                      </Action>
                      <Action title="Remove from tree" onClick={() => onAction("remove", s.campaignId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </Action>
                    </Box>
                  )}
                </Stack>
              );
            })}
          </Paper>
        );
      })}
    </Stack>
  );
};

export default TierGroups;
